import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, Truck, Shield, ChevronRight, ExternalLink, Instagram, ShoppingBag } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { products } from '../data/mockData';
import { getImageUrl } from '../lib/utils';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user, login } = useAuth();
  const { cart, clearCart, getCartTotal } = useCart();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showRedirectOptions, setShowRedirectOptions] = useState(false);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  // External links for order completion
  const SHOPIER_URL = 'https://www.shopier.com/valmera';
  const INSTAGRAM_URL = 'https://www.instagram.com/valmera.official';
  const IKAS_URL = 'https://valmera.ikas.store';

  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    address: '',
    city: '',
    district: '',
    postalCode: '',
    notes: '',
  });

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/giris', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || prev.name,
        phone: user.phone || prev.phone,
      }));
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const cartItems = cart.map(item => {
    const product = products.find(p => p.id === item.id);
    return {
      ...item,
      product,
      image: product?.image,
      productName: product?.name,
    };
  });

  const subtotal = getCartTotal();
  const shippingCost = subtotal >= 500 ? 0 : 50;
  const total = subtotal + shippingCost;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate
    if (!formData.name || !formData.phone || !formData.address || !formData.city || !formData.district) {
      setError('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    if (cart.length === 0) {
      setError('Sepetiniz boş');
      return;
    }

    // Save user address to profile
    try {
      const token = localStorage.getItem('session_token');
      await fetch(`${backendUrl}/api/user/profile`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          district: formData.district,
          postal_code: formData.postalCode,
        }),
      });
    } catch (err) {
      console.error('Profile update failed:', err);
    }

    // Show redirect options modal
    setShowRedirectOptions(true);
  };

  const handleRedirect = (platform) => {
    // Clear cart and redirect
    clearCart();
    
    let url = '';
    switch(platform) {
      case 'shopier':
        url = SHOPIER_URL;
        break;
      case 'instagram':
        url = INSTAGRAM_URL;
        break;
      case 'ikas':
        url = IKAS_URL;
        break;
      default:
        url = SHOPIER_URL;
    }
    
    window.open(url, '_blank');
    navigate('/', { replace: true });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
    }).format(price);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-32 pb-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-light mb-4">Giriş Yapmanız Gerekiyor</h2>
          <button
            onClick={login}
            className="bg-black text-white px-8 py-3 text-sm tracking-wider hover:bg-gray-900"
          >
            GİRİŞ YAP
          </button>
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="min-h-screen pt-32 pb-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-light mb-4">Sepetiniz Boş</h2>
          <Link
            to="/urunler"
            className="inline-block bg-black text-white px-8 py-3 text-sm tracking-wider hover:bg-gray-900"
          >
            ALIŞVERİŞE BAŞLA
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <Link to="/sepet" className="inline-flex items-center gap-2 text-gray-600 hover:text-black mb-4">
            <ArrowLeft size={18} />
            Sepete Dön
          </Link>
          <h1 className="text-3xl font-light tracking-wider">SİPARİŞ TAMAMLA</h1>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 text-sm rounded">
            {error}
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit}>
              {/* Shipping Info */}
              <div className="bg-white p-6 shadow-sm mb-6">
                <h2 className="text-lg font-medium mb-6 flex items-center gap-2">
                  <Truck size={20} />
                  Teslimat Bilgileri
                </h2>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Ad Soyad *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-black"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Telefon *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="05XX XXX XX XX"
                      className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-black"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-600 mb-1">Adres *</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Mahalle, cadde, sokak, bina no, daire no..."
                      className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-black resize-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">İl *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-black"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">İlçe *</label>
                    <input
                      type="text"
                      name="district"
                      value={formData.district}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-black"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Posta Kodu</label>
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-black"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Sipariş Notu</label>
                    <input
                      type="text"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Opsiyonel"
                      className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-black"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white p-6 shadow-sm mb-6">
                <h2 className="text-lg font-medium mb-6 flex items-center gap-2">
                  <CreditCard size={20} />
                  Ödeme Yöntemi
                </h2>

                <div className="border border-black p-4 flex items-center gap-4">
                  <input type="radio" checked readOnly className="text-black" />
                  <div className="flex-1">
                    <p className="font-medium">Shopier ile Güvenli Ödeme</p>
                    <p className="text-sm text-gray-500">Kredi kartı, banka kartı veya havale ile ödeme yapın</p>
                  </div>
                  <img 
                    src="https://www.shopier.com/img/shopier-logo.png" 
                    alt="Shopier" 
                    className="h-8"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>

                <div className="mt-4 p-4 bg-gray-50 flex items-start gap-3">
                  <Shield size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-gray-600">
                    <p className="font-medium text-black mb-1">Güvenli Alışveriş</p>
                    <p>Tüm ödemeleriniz Shopier güvencesi altında 256-bit SSL şifreleme ile korunmaktadır.</p>
                  </div>
                </div>
              </div>

              {/* Submit Button - Mobile */}
              <div className="lg:hidden">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-black text-white py-4 text-sm tracking-wider hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  SİPARİŞİ ONAYLA
                  <ChevronRight size={18} />
                </button>
              </div>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 shadow-sm sticky top-32">
              <h2 className="text-lg font-medium mb-6">Sipariş Özeti</h2>

              {/* Items */}
              <div className="space-y-4 mb-6">
                {cartItems.map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <img
                      src={getImageUrl(item.image)}
                      alt={item.productName}
                      className="w-16 h-16 object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.productName}</p>
                      <p className="text-xs text-gray-500">{item.selectedSize} | {item.selectedColor}</p>
                      <p className="text-sm">{item.quantity} x {formatPrice(item.price)}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Ara Toplam</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Kargo</span>
                  <span className={shippingCost === 0 ? 'text-green-600' : ''}>
                    {shippingCost === 0 ? 'Ücretsiz' : formatPrice(shippingCost)}
                  </span>
                </div>
                {shippingCost > 0 && (
                  <p className="text-xs text-gray-500">
                    {formatPrice(500 - subtotal)} daha ekleyin, kargo ücretsiz!
                  </p>
                )}
                <div className="flex justify-between text-lg font-medium pt-2 border-t">
                  <span>Toplam</span>
                  <span>{formatPrice(total)}</span>
                </div>
              </div>

              {/* Submit Button - Desktop */}
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="hidden lg:flex w-full mt-6 bg-black text-white py-4 text-sm tracking-wider hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed items-center justify-center gap-2"
              >
                SİPARİŞİ ONAYLA
                <ChevronRight size={18} />
              </button>

              {/* Info */}
              <p className="text-xs text-gray-500 mt-4 text-center">
                Siparişi tamamlayarak{' '}
                <a href="#" className="underline">Satış Sözleşmesi</a>
                'ni kabul etmiş olursunuz.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Redirect Options Modal */}
      {showRedirectOptions && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-medium mb-2 text-center">Sipariş Bilgileri Kaydedildi!</h2>
            <p className="text-gray-600 text-center mb-6 text-sm">
              Ödeme işleminizi tamamlamak için aşağıdaki platformlardan birini seçin:
            </p>
            
            <div className="space-y-3">
              {/* Shopier */}
              <button
                onClick={() => handleRedirect('shopier')}
                className="w-full flex items-center justify-between p-4 border-2 border-black rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <ShoppingBag size={24} />
                  <div className="text-left">
                    <p className="font-medium">Shopier ile Ödeme</p>
                    <p className="text-xs text-gray-500">Kredi kartı, banka kartı, havale</p>
                  </div>
                </div>
                <ExternalLink size={18} className="text-gray-400" />
              </button>

              {/* Instagram */}
              <button
                onClick={() => handleRedirect('instagram')}
                className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Instagram size={24} className="text-pink-600" />
                  <div className="text-left">
                    <p className="font-medium">Instagram'dan Sipariş</p>
                    <p className="text-xs text-gray-500">DM ile iletişime geçin</p>
                  </div>
                </div>
                <ExternalLink size={18} className="text-gray-400" />
              </button>

              {/* ikas */}
              <button
                onClick={() => handleRedirect('ikas')}
                className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <ShoppingBag size={24} className="text-blue-600" />
                  <div className="text-left">
                    <p className="font-medium">ikas Mağazası</p>
                    <p className="text-xs text-gray-500">Online mağazamızı ziyaret edin</p>
                  </div>
                </div>
                <ExternalLink size={18} className="text-gray-400" />
              </button>
            </div>

            <button
              onClick={() => setShowRedirectOptions(false)}
              className="w-full mt-4 py-2 text-sm text-gray-500 hover:text-gray-700"
            >
              İptal
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;
